import api from '../api/client';
import type {
  CpcFactorSet,
  CpcFactorSetDetail,
  CpcRoleRate,
  CpcCalculationListItem,
  CpcCalculation,
  CpcActualLogEntry,
  CpcClockodoConfig,
  UpdateClockodoConfigPayload,
  CpcClockodoServiceMapping,
  CreateServiceMappingPayload,
  ClockodoService,
  LinkExistingProjectPayload,
  CreateClockodoProjectPayload,
  ClockodoSyncHealth,
  PlanVsIstItem,
  CalculateAssessmentPayload,
  SetActualPtPayload,
  CalculateAssessmentResponse,
  CreateFactorSetPayload,
  UpdateFactorSetPayload,
  CreateRoleRatePayload,
  UpdateRoleRatePayload,
  AuditLogPage,
  AuditLogFilter,
} from './cpc-types';

export const cpcApi = {
  // Factor Sets
  listFactorSets: () =>
    api.get<CpcFactorSet[]>('/ctb/factor-sets').then((r) => r.data),

  getActiveFactorSet: () =>
    api.get<CpcFactorSetDetail>('/ctb/factor-sets/active').then((r) => r.data),

  getFactorSet: (id: string) =>
    api.get<CpcFactorSetDetail>(`/ctb/factor-sets/${id}`).then((r) => r.data),

  createFactorSet: (payload: CreateFactorSetPayload) =>
    api.post<CpcFactorSet>('/ctb/factor-sets', payload).then((r) => r.data),

  updateFactorSet: (id: string, data: UpdateFactorSetPayload) =>
    api.patch<CpcFactorSetDetail>(`/ctb/factor-sets/${id}`, data).then((r) => r.data),

  deleteFactorSet: (id: string) =>
    api.delete(`/ctb/factor-sets/${id}`).then(() => {}),

  activateFactorSet: (id: string) =>
    api.post<CpcFactorSet>(`/ctb/factor-sets/${id}/activate`).then((r) => r.data),

  // Kalkulationen
  calculate: (companyId: string, payload: CalculateAssessmentPayload) =>
    api
      .post<CalculateAssessmentResponse>(`/ctb/companies/${companyId}/assessment/calculate`, payload)
      .then((r) => r.data),

  listCalculations: (companyId: string) =>
    api
      .get<CpcCalculationListItem[]>(`/ctb/companies/${companyId}/calculations`)
      .then((r) => r.data),

  getCalculation: (id: string) =>
    api.get<CpcCalculation>(`/ctb/calculations/${id}`).then((r) => r.data),

  deleteCalculation: (id: string) =>
    api.delete(`/ctb/calculations/${id}`).then(() => {}),

  setActualPt: (id: string, payload: SetActualPtPayload) =>
    api.post<CpcCalculation>(`/ctb/calculations/${id}/actual`, payload).then((r) => r.data),

  // Tagessätze
  getRoleRates: () =>
    api.get<CpcRoleRate[]>('/ctb/role-rates').then((r) => r.data),

  getAllRoleRates: () =>
    api.get<CpcRoleRate[]>('/ctb/role-rates/all').then((r) => r.data),

  createRoleRate: (payload: CreateRoleRatePayload) =>
    api.post<CpcRoleRate>('/ctb/role-rates', payload).then((r) => r.data),

  updateRoleRate: (id: string, payload: UpdateRoleRatePayload) =>
    api.patch<CpcRoleRate>(`/ctb/role-rates/${id}`, payload).then((r) => r.data),

  deleteRoleRate: (id: string) =>
    api.delete(`/ctb/role-rates/${id}`).then(() => {}),

  // Clockodo: Kalkulations-Verknüpfung
  linkExistingProject: (calcId: string, payload: LinkExistingProjectPayload) =>
    api.post(`/ctb/calculations/${calcId}/clockodo/link-existing`, payload).then((r) => r.data),

  createClockodoProject: (calcId: string, payload: CreateClockodoProjectPayload) =>
    api.post(`/ctb/calculations/${calcId}/clockodo/create-project`, payload).then((r) => r.data),

  manualSync: (calcId: string) =>
    api.post(`/ctb/calculations/${calcId}/clockodo/sync`).then((r) => r.data),

  getActualLogEntries: (calcId: string) =>
    api.get<CpcActualLogEntry[]>(`/ctb/calculations/${calcId}/clockodo/entries`).then((r) => r.data),

  // Clockodo: Admin
  getClockodoConfig: () =>
    api.get<CpcClockodoConfig>('/ctb/clockodo/config').then((r) => r.data),

  updateClockodoConfig: (payload: UpdateClockodoConfigPayload) =>
    api.patch<CpcClockodoConfig>('/ctb/clockodo/config', payload).then((r) => r.data),

  getClockodoServices: () =>
    api.get<ClockodoService[]>('/ctb/clockodo/services').then((r) => r.data),

  getServiceMappings: () =>
    api.get<CpcClockodoServiceMapping[]>('/ctb/clockodo/service-mappings').then((r) => r.data),

  createServiceMapping: (payload: CreateServiceMappingPayload) =>
    api.post<CpcClockodoServiceMapping>('/ctb/clockodo/service-mappings', payload).then((r) => r.data),

  deleteServiceMapping: (id: string) =>
    api.delete(`/ctb/clockodo/service-mappings/${id}`).then(() => {}),

  getSyncHealth: () =>
    api.get<ClockodoSyncHealth>('/ctb/clockodo/health').then((r) => r.data),

  // Reporting
  getPlanVsIst: () =>
    api.get<PlanVsIstItem[]>('/ctb/reporting/plan-vs-ist').then((r) => r.data),

  // Audit-Log
  getAuditLog: (filter: AuditLogFilter = {}) => {
    const params: Record<string, string> = {};
    if (filter.entityType) params.entityType = filter.entityType;
    if (filter.from) params.from = filter.from;
    if (filter.to) params.to = filter.to;
    if (filter.search) params.search = filter.search;
    if (filter.page != null) params.page = String(filter.page);
    if (filter.pageSize != null) params.pageSize = String(filter.pageSize);
    return api.get<AuditLogPage>('/ctb/audit-log', { params }).then((r) => r.data);
  },

  getAuditLogExportUrl: (filter: AuditLogFilter = {}) => {
    const params = new URLSearchParams();
    if (filter.entityType) params.set('entityType', filter.entityType);
    if (filter.from) params.set('from', filter.from);
    if (filter.to) params.set('to', filter.to);
    if (filter.search) params.set('search', filter.search);
    const q = params.toString();
    return `/ctb/audit-log/export${q ? `?${q}` : ''}`;
  },
};
